<template>
  <div class="min-h-screen bg-bg font-sans text-text-primary">
    <SiteHeader />

    <main>
      <!-- Hero -->
      <section class="pt-16 md:pt-24 pb-16 md:pb-24">
        <div class="max-w-7xl mx-auto px-4 md:px-8">
          <div class="max-w-3xl">
            <div class="inline-flex items-center gap-2 badge-brutal bg-brutal-lavender mb-6">
              <span class="w-2 h-2 rounded-full bg-text-primary animate-pulse" />
              <span>{{ $t('hero.badge') }}</span>
            </div>

            <h1 class="text-4xl md:text-6xl font-bold tracking-tight mb-6">
              {{ $t('hero.title') }}
            </h1>
            <p class="text-xl md:text-2xl text-text-secondary leading-relaxed mb-8">
              {{ $t('hero.subtitle') }}
            </p>
            <p class="text-text-regular leading-relaxed mb-10 max-w-2xl">
              {{ $t('hero.description') }}
            </p>

            <div class="flex flex-wrap items-center gap-4">
              <a href="#products" class="btn-brutal btn-brutal-primary">
                {{ $t('hero.ctaPrimary') }}
              </a>
              <a href="mailto:hello@jiker.ai" class="btn-brutal btn-brutal-ghost">
                {{ $t('hero.ctaSecondary') }}
              </a>
            </div>
          </div>
        </div>
      </section>

      <SectionMission />

      <!-- Products -->
      <section id="products" class="py-16 md:py-24">
        <div class="max-w-7xl mx-auto px-4 md:px-8">
          <div class="mb-10 md:mb-14">
            <h2 class="text-3xl md:text-4xl font-bold mb-3">{{ $t('products.title') }}</h2>
            <p class="text-text-secondary text-lg max-w-2xl">
              {{ $t('products.description') }}
            </p>
          </div>

          <div class="space-y-12">
            <!-- AI Infra -->
            <div>
              <div class="mb-6">
                <h3 class="text-2xl md:text-3xl font-bold mb-2">{{ $t('products.aiInfra.title') }}</h3>
                <p class="text-text-secondary text-lg max-w-2xl">
                  {{ $t('products.aiInfra.description') }}
                </p>
              </div>

              <div class="space-y-6">
                <!-- looploop -->
                <div>
                  <p class="text-sm font-medium text-text-secondary mb-3">{{ $t('products.aiInfra.looploop.name') }}</p>
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <ProductCard
                      v-for="(product, index) in aiInfra.looploop"
                      :key="product.titleKey"
                      :title="$t(`products.aiInfra.looploop.items[${index}].title`)"
                      :subtitle="$t(`products.aiInfra.looploop.items[${index}].subtitle`)"
                      :description="$t(`products.aiInfra.looploop.items[${index}].description`)"
                      :href="product.href"
                      :tags="$tm(`products.aiInfra.looploop.items[${index}].tags`)"
                      :variant="product.variant"
                      :icon="product.icon"
                    />
                  </div>
                </div>

                <!-- llmpool -->
                <div>
                  <p class="text-sm font-medium text-text-secondary mb-3">{{ $t('products.aiInfra.llmpool.name') }}</p>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ProductCard
                      v-for="(product, index) in aiInfra.llmpool"
                      :key="product.titleKey"
                      :title="$t(`products.aiInfra.llmpool.items[${index}].title`)"
                      :description="$t(`products.aiInfra.llmpool.items[${index}].description`)"
                      :href="product.href"
                      :tags="$tm(`products.aiInfra.llmpool.items[${index}].tags`)"
                      :variant="product.variant"
                      :icon="product.icon"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Talent Infra -->
            <div>
              <div class="mb-6">
                <h3 class="text-2xl md:text-3xl font-bold mb-2">{{ $t('products.talentInfra.title') }}</h3>
                <p class="text-text-secondary text-lg max-w-2xl">
                  {{ $t('products.talentInfra.description') }}
                </p>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ProductCard
                  v-for="(product, index) in talentInfra"
                  :key="product.titleKey"
                  :title="$t(`products.talentInfra.items[${index}].title`)"
                  :description="$t(`products.talentInfra.items[${index}].description`)"
                  :href="product.href"
                  :tags="$tm(`products.talentInfra.items[${index}].tags`)"
                  :variant="product.variant"
                  :icon="product.icon"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Stories -->
      <section id="stories" class="py-16 md:py-24 bg-background-white">
        <div class="max-w-7xl mx-auto px-4 md:px-8">
          <div class="mb-10 md:mb-14">
            <h2 class="text-3xl md:text-4xl font-bold mb-3">{{ $t('stories.title') }}</h2>
            <p class="text-text-secondary text-lg max-w-2xl">
              {{ $t('stories.description') }}
            </p>
          </div>

          <div class="space-y-10">
            <div v-for="group in storyGroups" :key="group.key">
              <p class="text-sm font-medium text-text-secondary mb-4">{{ $t(`stories.${group.key}.title`) }}</p>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <CaseStudyCard
                  v-for="(item, index) in group.items"
                  :key="index"
                  :title="$t(`stories.${group.key}.items[${index}].title`)"
                  :category="$t(`stories.${group.key}.items[${index}].category`)"
                  :description="$t(`stories.${group.key}.items[${index}].description`)"
                  :result="$t(`stories.${group.key}.items[${index}].result`)"
                  :variant="item.variant"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionAbout />

      <!-- CTA -->
      <section class="py-16 md:py-24">
        <div class="max-w-7xl mx-auto px-4 md:px-8">
          <div class="card-brutal bg-primary p-8 md:p-12 text-center">
            <h2 class="text-3xl md:text-4xl font-bold mb-4">
              {{ $t('cta.title') }}
            </h2>
            <p class="text-text-regular text-lg max-w-2xl mx-auto mb-8">
              {{ $t('cta.description') }}
            </p>
            <div class="flex flex-wrap items-center justify-center gap-4">
              <a
                href="mailto:hello@jiker.ai"
                class="btn-brutal bg-background-white"
              >
                {{ $t('cta.primary') }}
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>

<script setup>
import { watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import SiteHeader from './components/SiteHeader.vue';
import SiteFooter from './components/SiteFooter.vue';
import ProductCard from './components/ProductCard.vue';
import CaseStudyCard from './components/CaseStudyCard.vue';
import SectionMission from './components/SectionMission.vue';
import SectionAbout from './components/SectionAbout.vue';

const { t, locale } = useI18n();

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setOg(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function updateHead() {
  const title = t('hero.title');
  document.title = `${title} | JIKER`;
  setMeta('description', t('hero.description'));
  setOg('og:title', title);
  setOg('og:description', t('hero.description'));
}

watch(locale, updateHead);
onMounted(updateHead);

const aiInfra = {
  looploop: [
    { titleKey: 'looploop.chat', href: 'https://looploop.chat', variant: 'primary', icon: 'chat' },
    { titleKey: 'looploop.run', href: 'https://looploop.run', variant: 'primary', icon: 'run' },
    { titleKey: 'looploop.vip', href: 'https://looploop.vip', variant: 'primary', icon: 'cloud' },
  ],
  llmpool: [
    { titleKey: 'llmpool.io', href: 'https://llmpool.io', variant: 'lavender', icon: 'pool' },
  ],
};

const talentInfra = [
  { titleKey: 'geek-academy', href: 'https://jikexueyuan.com', variant: 'red', icon: 'school' },
  { titleKey: 'geek-future-school', href: 'https://jiker.com', variant: 'red', icon: 'school' },
];

const storyGroups = [
  {
    key: 'energy',
    items: [
      { variant: 'accent' },
      { variant: 'primary' },
      { variant: 'lavender' },
      { variant: 'pink' },
    ],
  },
  {
    key: 'ecommerce',
    items: [{ variant: 'primary' }, { variant: 'accent' }],
  },
  {
    key: 'finance',
    items: [{ variant: 'lavender' }, { variant: 'primary' }],
  },
  {
    key: 'education',
    items: [{ variant: 'pink' }, { variant: 'accent' }],
  },
  {
    key: 'exploring',
    items: [{ variant: 'accent' }],
  },
];
</script>
